import { Configuration } from "../utils/configuration";
import { APIOptions } from "./hooks/useApiBase";
import { useApiLazy, UseAPILazyProps } from "./hooks/useApiLazy";
import {
  CreateStateRequest,
  CreateStateResponse,
  EC2Municipality,
  GetDocumentListByMunicipalityResponse,
  RenameDocumentRequest,
  StateMunicipalityRequest,
  States,
} from "./types/documentationCatalogs.types";

/**
 * Config
 */
const hostOverride = Configuration.ec2Url;
const ec2Key = Configuration.ec2Key;

/**
 * Hooks
 */
// 🟦 GET /states - Get all states
export function useApiGetStates(options: APIOptions<States[]> = {}) {
  const props: UseAPILazyProps<States[]> = {
    url: "states",
    kind: "get",
    options,
    header: {
      "api-key": ec2Key,
    },
    hostOverride,
  };
  const api = useApiLazy<States[]>(props);
  const call = async () => await api.call({ ...props });
  return { ...api, call };
}

// 🟦 GET /municipalities/{stateId} - Get municipalities by state
export function useApiGetMunicipalitiesByState(
  options: APIOptions<EC2Municipality[]> = {}
) {
  const props: UseAPILazyProps<EC2Municipality[]> = {
    url: "",
    kind: "get",
    options,
    header: {
      "api-key": ec2Key,
    },
    hostOverride,
  };
  const api = useApiLazy<EC2Municipality[]>(props);
  const call = async (stateId: number) =>
    await api.call({
      ...props,
      url: `municipalities/${stateId}`,
    });
  return { ...api, call };
}

// 🟦 GET /documents/{municipalityId} - Get documents by municipality
export function useApiGetDocumentListByMunicipality(
  options: APIOptions<GetDocumentListByMunicipalityResponse> = {}
) {
  const props: UseAPILazyProps<GetDocumentListByMunicipalityResponse> = {
    url: "",
    kind: "get",
    options,
    header: {
      "api-key": ec2Key,
    },
    hostOverride,
  };
  const api = useApiLazy<GetDocumentListByMunicipalityResponse>(props);
  const call = async (municipalityId: number) =>
    await api.call({
      ...props,
      url: `documents/${municipalityId}`,
    });
  return { ...api, call };
}

// 🟧 PUT /download/document/{documentId} - Download document by municipality
export function useApiGetDocumentByMunicipality(
  options: APIOptions<Blob> = {}
) {
  const props: UseAPILazyProps<Blob> = {
    url: "",
    kind: "put",
    options: { ...options, responseType: "blob" },
    header: {
      "api-key": ec2Key,
    },
    hostOverride,
  };
  const api = useApiLazy<Blob>(props);
  const call = async (documentId: number) =>
    await api.call({
      ...props,
      url: `download/document/${documentId}`,
    });
  return { ...api, call };
}

// 🟧 PUT /stateMunicipality - Submit municipality info
export function useApiPutStateMunicipality(options: APIOptions<string> = {}) {
  const props: UseAPILazyProps<string, StateMunicipalityRequest> = {
    url: "stateMunicipality",
    kind: "put",
    options,
    header: {
      "api-key": ec2Key,
    },
    hostOverride,
  };
  const api = useApiLazy<string, StateMunicipalityRequest>(props);
  const call = async (body: StateMunicipalityRequest) =>
    await api.call({
      ...props,
      body,
    });
  return { ...api, call };
}

// 🟧 PUT //register-state - Create new state
export function useApiCreateState(
  options: APIOptions<CreateStateResponse> = {}
) {
  const props: UseAPILazyProps<CreateStateResponse, CreateStateRequest> = {
    url: "register-state",
    kind: "put",
    options,
    header: {
      "api-key": ec2Key,
    },
    hostOverride,
  };
  const api = useApiLazy<CreateStateResponse, CreateStateRequest>(props);
  const call = async (body: CreateStateRequest) =>
    await api.call({
      ...props,
      body,
    });
  return { ...api, call };
}

// 🟥 DELETE /stateMunicipality/{municipalityId} - Delete state municipality
export function useApiDeleteStateMunicipality(
  options: APIOptions<string> = {}
) {
  const props: UseAPILazyProps<string> = {
    url: "",
    kind: "delete",
    options,
    header: {
      "api-key": ec2Key,
    },
    hostOverride,
  };
  const api = useApiLazy<string>(props);
  const call = async (municipalityId: number) =>
    await api.call({
      ...props,
      url: `stateMunicipality/${municipalityId}`,
    });
  return { ...api, call };
}

// 🟧 PUT /rename/document/{documentId} - Rename document
export function useApiRenameDocument(options: APIOptions<string> = {}) {
  const props: UseAPILazyProps<string, RenameDocumentRequest> = {
    url: "",
    kind: "put",
    options,
    header: {
      "api-key": ec2Key,
    },
    hostOverride,
  };
  const api = useApiLazy<string, RenameDocumentRequest>(props);
  const call = async (documentId: number, newName: string) =>
    await api.call({
      ...props,
      url: `rename/document/${documentId}`,
      body: { documentId, newName },
    });
  return { ...api, call };
}
